import { ReactElement, useCallback, useEffect, useRef, useState } from 'react';
import fileDownload from 'js-file-download';
import { Header } from '../shared/header/header';
import { SortOptions } from '../../config/supplierTabConfig';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import SearchIcon from '@mui/icons-material/Search';
import DeleteIcon from '@mui/icons-material/Delete';
import { CustomTab } from '../shared/tab/tab';
import { SingleSelectDropDown } from '../shared/dropdown/singleSelect';
import { makeStyles } from '@mui/styles';
import { Search } from '../shared/search/search';
import { CustomButton } from '../shared/button/button';
import { SupplierFilter, SupplierInfoResponse, SupplierMaster, SupplierType } from '../../types/supplier.types';
import { Typography } from '@mui/material';
import { colors, theme } from '../../theme';
import { Panel } from '../shared/panel/panel';
import { SupplierEditPage } from './supplierEdit';
import { SupplierTable } from './supplierTable';
import { MultiSelectDropDown } from '../shared/dropdown/multiSelect';
import { ToolTip } from '../shared/tooltip/tooltip';
import { useAsyncOperation } from '../../utils/use-async-operation';
import { exportData, getSupplierInfoApi, getTTMaster } from '../../services/supplier-service';
import { Loader } from '../shared/loader/loader';
import { CustomAlert } from '../shared/alert/alert';
import { TraceabilityOverview } from 'src/components/supplier/traceabilityOverview';

const color = colors();
const themes = theme().typography;

export const Supplier = (): ReactElement => {
	
	const classes = useStyles();
	const BLANKS: string = '(Blanks)';
	const TRACEABILITY_OVERVIEW_TAB_KEY: number = -99;
	const isAdmin: boolean = localStorage.getItem('roles')?.includes('TT.Admin') || false;
	
	const [activeTab, setActiveTab] = useState<number>(0);
	const [supplierList, setSupplierList] = useState<SupplierType[]>([]);
	const [selectedRowNum, setSelectedRowNum] = useState<number[]>([]);
	const [selectedRow, setSelectedRow] = useState<SupplierType[]>([]);
	const [activeRow, setActiveRow] = useState<SupplierType>();
	const [platform, setPlatform] = useState<string[]>([]);
	const [scopeStatus, setScopeStatus] = useState<string[]>([]);
	const [spurStatus, setSpurStatus] = useState<string[]>([]);
	const [supplierType, setSupplierType] = useState<string[]>([]);
	const [location, setLocation] = useState<string[]>([]);
	const [searchText, setSearchText] = useState<string>('');
	const [sortOption, setSortOption] = useState<string>('5');
	const [openPanel, setOpenPanel] = useState<boolean>(false);
	const [isRowEdit, setIsRowEdit] = useState<boolean>(false);
	const [isSamePlatform, setIsSamePlatform] = useState<boolean>(false);
	const [appliedFilter, setAppliedFilter] = useState<SupplierFilter>();
	const [currPage, setCurrPage] = useState<number>(1);
	const [rowsPerPage, setRowsPerPage] = useState<number>(25);
	const [startPageLoad, setStartPageLoad] = useState(true);
	const [errorAlertOpen, setErrorAlertOpen] = useState(false);
	const [isSave, setIsSave] = useState(false);
	const [successAlertOpen, setSuccessAlertOpen] = useState(false);
	const [exportBusy, setExportBusy] = useState(false);
	const [exportError, setExportError] = useState(false);
	const [supplierCategory, setSupplierCategory] = useState<string>('0');
	const initialLoadTriggered = useRef<boolean>(false);
	const lastFetchedTab = useRef<number | null>(null);
	const [getSupplierInfo, , supplierInfo, supplierError] = useAsyncOperation<SupplierInfoResponse>(getSupplierInfoApi);
	const [getTTMasterData, , ttMasterData] = useAsyncOperation<SupplierMaster>(getTTMaster);
	
	useEffect(() => {
		if (initialLoadTriggered.current) return;
		initialLoadTriggered.current = true;
		lastFetchedTab.current = activeTab;
		getSupplierInfo({
			page: 1,
			recordPerPage: 25,
			searchText: '',
			isFilterApplied: false,
			sortBy: '5',
			supplierCategory: parseInt(supplierCategory),
		} as SupplierFilter);
		getTTMasterData();
	}, []); // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		if (supplierError) {
			setStartPageLoad(false);
			setErrorAlertOpen(true);
		}
	}, [supplierError]);

	useEffect(() => {
		setSelectedRow([]);
		let data: SupplierType[] = [];
		selectedRowNum.map((i) => data.push(supplierList.filter((s) => s.id === i)[0]));
		setSelectedRow(data);

		let pfIds = data.map((row) => row.platform?.tpId);
		let pfs = data.map((row) => row.platforms?.length);
		setIsSamePlatform(new Set(pfIds).size === 1 && new Set(pfs).size === 1);
	}, [selectedRowNum]); // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		if (lastFetchedTab.current === activeTab) return;
		lastFetchedTab.current = activeTab;
		if (activeTab === TRACEABILITY_OVERVIEW_TAB_KEY) return;
		setSelectedRowNum([]);
		setCurrPage(1);
		let platformIds =
			activeTab === 0
				? ttMasterData?.platforms?.filter((item) => platform.includes(item.value!))?.map((m) => m.key)
				: [activeTab];

		let scopeStatusIds = ttMasterData?.scopeStatuses
			?.filter((item) => scopeStatus.includes(item.value!))
			?.map((m) => m.key);
		scopeStatusIds = scopeStatus.includes(BLANKS) ? [...(scopeStatusIds || []), 0] : scopeStatusIds;

		let supplierTypeIds = ttMasterData?.supplierTypes
			?.filter((item) => supplierType.includes(item.suTypeName))
			?.map((m) => m.suTypeId);

		let poIds = ttMasterData?.poIds?.filter((item) => location.includes(item))?.map((m) => m);

		setStartPageLoad(true);
		getSupplierInfo({
			page: 1,
			recordPerPage: rowsPerPage,
			searchText: searchText,
			isFilterApplied: !(
				!platformIds?.length &&
				!scopeStatusIds?.length &&
				!spurStatus?.length &&
				!supplierTypeIds?.length &&
				!supplierType?.length &&
				!poIds?.length &&
				searchText.length === 0
			),
			platforms: !platformIds?.length ? undefined : platformIds,
			scopeStatusIds: !scopeStatusIds?.length ? undefined : scopeStatusIds,
			supplierStatuses: !spurStatus?.length ? undefined : spurStatus,
			suTypes: !supplierTypeIds?.length ? undefined : supplierTypeIds,
			suTypeNames: !supplierType?.length ? undefined : supplierType,
			poIds: !poIds?.length ? undefined : poIds,
			supplierCategory: parseInt(supplierCategory),
			sortBy: sortOption,
		} as SupplierFilter);
	}, [activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		if (appliedFilter) {
			appliedFilter.page = currPage;
			appliedFilter.recordPerPage = rowsPerPage;
		}
	}, [currPage, rowsPerPage]); // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		if (supplierInfo) {
			supplierInfo.supplierInfo.map((s) => (s.isActive = s.supplierStatus?.toLocaleLowerCase() === 'active'));
			setSupplierList(supplierInfo.supplierInfo);
			setStartPageLoad(false);
			setSelectedRowNum([...selectedRowNum]);
			setSuccessAlertOpen(isSave);
		}
	}, [supplierInfo, ttMasterData]); // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		let platformIds =
			activeTab === 0
				? ttMasterData?.platforms?.filter((item) => platform.includes(item.value!))?.map((m) => m.key)
				: [activeTab];

		let scopeStatusIds = ttMasterData?.scopeStatuses
			?.filter((item) => scopeStatus.includes(item.value!))
			?.map((m) => m.key);
		scopeStatusIds = scopeStatus.includes(BLANKS) ? [...(scopeStatusIds || []), 0] : scopeStatusIds;

		let supplierTypeIds = ttMasterData?.supplierTypes
			?.filter((item) => supplierType.includes(item.suTypeName))
			?.map((m) => m.suTypeId);

		let poIds = ttMasterData?.poIds?.filter((item) => location.includes(item))?.map((m) => m);

		setAppliedFilter({
			page: currPage,
			recordPerPage: rowsPerPage,
			platforms: !platformIds?.length ? undefined : platformIds,
			scopeStatusIds: !scopeStatusIds?.length ? undefined : scopeStatusIds,
			supplierStatuses: !spurStatus?.length ? undefined : spurStatus,
			suTypes: !supplierTypeIds?.length ? undefined : supplierTypeIds,
			suTypeNames: !supplierType?.length ? undefined : supplierType,
			poIds: !poIds?.length ? undefined : poIds,
			searchText: searchText,
			supplierCategory: parseInt(supplierCategory),
			isFilterApplied: !(
				!platformIds?.length &&
				!scopeStatusIds?.length &&
				!spurStatus?.length &&
				!supplierTypeIds?.length &&
				!supplierType?.length &&
				!poIds?.length &&
				searchText.length === 0
			),
		});
	}, [platform, scopeStatus, spurStatus, supplierType, location, searchText, supplierCategory]); // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		if (isSave) onPanelClose();
	}, [isSave]); // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		if (!successAlertOpen) setIsSave(false);
	}, [successAlertOpen]);

	const onEditClick = useCallback((isRowEdit: boolean) => {
		setOpenPanel(true);
		setIsSave(false);
		setIsRowEdit(isRowEdit);
	}, []);

	const handleSearch = (isReset: boolean = false) => {
		setStartPageLoad(true);
		if (appliedFilter) {
			if (isReset) {
				appliedFilter.page = 1;
				setCurrPage(1);
			}
			appliedFilter.sortBy = sortOption;
			if (activeTab === 0) {
				if (platform && platform?.length > 0)
					appliedFilter.platforms = ttMasterData?.platforms
						?.filter((item) => platform.includes(item.value!))
						?.map((m) => m.key);
				else
					appliedFilter.platforms = ttMasterData?.platforms
						?.filter((item) => item.key === activeTab)
						?.map((m) => m.key);
			} else
				appliedFilter.platforms = ttMasterData?.platforms?.filter((item) => item.key === activeTab)?.map((m) => m.key);

			appliedFilter.platforms =
				appliedFilter.platforms?.length === 1 && appliedFilter.platforms[0] === 0 ? undefined : appliedFilter.platforms;
		}

		getSupplierInfo(appliedFilter);
	};

	const onClearClick = () => {
		setCurrPage(1);
		if (typeof window !== 'undefined' && typeof window.scrollTo === 'function') {
			try {
				window.scrollTo({ top: 0, behavior: 'smooth' });
			} catch {
				window.scrollTo(0, 0);
			}
		}
		setPlatform([]);
		setScopeStatus([]);
		setSpurStatus([]);
		setSupplierType([]);
		setLocation([]);
		setSupplierCategory('0');
		setSearchText('');
		setStartPageLoad(true);
		getSupplierInfo({
			page: 1,
			recordPerPage: rowsPerPage,
			searchText: '',
			supplierCategory: -1,
			sortBy: sortOption,
			isFilterApplied: activeTab !== 0,
			platforms: activeTab === 0 ? undefined : [activeTab],
		} as SupplierFilter);
	};

	const onPanelClose = () => {
		setOpenPanel(false);
		setIsRowEdit(false);
		if (isSave) {
			handleSearch(true);
		}
	};

	return (
		<div>
			<Loader
				key="supplier-loader"
				open={startPageLoad || exportBusy}
				message={exportBusy ? 'Exporting Data...' : 'Loading...'}
			/>
			<Header title="External Traceability Platforms" />
			{errorAlertOpen && (
				<CustomAlert
					open={errorAlertOpen}
					message="Something went wrong while retrieving supplier data!"
					severity="error"
					timeout={5000}
					isFade={false}
					onCloseCallback={() => {
						setErrorAlertOpen(false);
					}}
				/>
			)}
			<div className={classes.topPadding}>
				<CustomTab
					data={
						ttMasterData
							? [
									...ttMasterData
										.platforms!.filter((s) => s.key !== 2)
										.map((s) => ({ ...s, value: s.value === 'All' ? 'Supplier Onboarding' : s.value })),
									//{ key: TRACEABILITY_OVERVIEW_TAB_KEY, value: 'Traceability Overview' },
							  ]
							: []
					}
					value={activeTab}
					onChangeCallback={setActiveTab}
				/>
			</div>
			{activeTab === TRACEABILITY_OVERVIEW_TAB_KEY ? (
				<TraceabilityOverview />
			) : (
				<>
					<div className={classes.topPadding}>
						<div className={classes.dropdownContainer}>
							{activeTab === 0 && (
								<MultiSelectDropDown
									label="Platform"
									data={
										ttMasterData &&
										ttMasterData?.platforms?.filter((x) => x.key !== 0 && x.key !== 2).map((list) => list?.value!)
									}
									value={platform}
									onSelectCallback={setPlatform}
								/>
							)}
							<MultiSelectDropDown
								label="Scope Status"
								data={ttMasterData && [...(ttMasterData?.scopeStatuses?.map((list) => list.value!) || []), BLANKS]}
								value={scopeStatus}
								onSelectCallback={setScopeStatus}
							/>
							<MultiSelectDropDown
								label="Spur Status"
								data={ttMasterData && ttMasterData?.supplierStatuses?.map((list) => list.supuStatusName)}
								value={spurStatus}
								onSelectCallback={setSpurStatus}
							/>
							<MultiSelectDropDown
								label="Supplier Type"
								data={ttMasterData && ttMasterData?.supplierTypes?.map((list) => list.suTypeName)}
								value={supplierType}
								onSelectCallback={setSupplierType}
							/>
							<SingleSelectDropDown
								label=""
								data={[
									{ key: 0, value: 'Category', disabled: true },
									{ key: -1, value: 'All' },
									{ key: 1, value: 'Commercial' },
									{ key: 2, value: 'Non-Commercial' },
								]}
								value={supplierCategory}
								onSelectCallback={(selectedValue) => setSupplierCategory(selectedValue)}
							/>
							<MultiSelectDropDown
								label="Production Office"
								data={ttMasterData && ttMasterData.poIds}
								value={location}
								onSelectCallback={setLocation}
							/>
							<Search
								placeholder="Supplier Name, Spur Id..."
								value={searchText}
								onSearchCallback={setSearchText}
								onEnterSearch={() => handleSearch(true)}
							/>

							<CustomButton
								id="search"
								text=""
								startIcon={<SearchIcon />}
								width={90}
								variant="outlined"
								onClickCallback={() => handleSearch(true)}
							/>
							{(platform.length > 0 ||
								scopeStatus.length > 0 ||
								spurStatus.length > 0 ||
								supplierType.length > 0 ||
								location.length > 0 ||
								supplierCategory !== '0' ||
								searchText) && (
								<CustomButton id="clear" text="" startIcon={<DeleteIcon />} width={90} onClickCallback={onClearClick} />
							)}
						</div>
						<ToolTip
							tooltipText="Export to Excel"
							children={
								<img
									alt="export"
									src={require('../../images/excel.png')}
									className={classes.export}
									onClick={() => {
										setExportBusy(true);
										let filters: SupplierFilter;
										if (appliedFilter) {
											filters = { ...appliedFilter };
											filters.page = 1;
											filters.sortBy = sortOption;
											if (activeTab === 0) {
												if (platform && platform?.length > 0)
													filters.platforms = ttMasterData?.platforms
														?.filter((item) => platform.includes(item.value!))
														?.map((m) => m.key);
												else
													filters.platforms = ttMasterData?.platforms
														?.filter((item) => item.key === activeTab)
														?.map((m) => m.key);
											} else
												filters.platforms = ttMasterData?.platforms
													?.filter((item) => item.key === activeTab)
													?.map((m) => m.key);

											filters.platforms =
												appliedFilter.platforms?.length === 1 && appliedFilter.platforms[0] === 0
													? undefined
													: appliedFilter.platforms;
										} else {
											filters = {
												page: 1,
												recordPerPage: 25,
												searchText: '',
												isFilterApplied: false,
												sortBy: '5',
												supplierCategory: parseInt(supplierCategory),
											} as SupplierFilter;
										}

										exportData(filters)
											.then((res) => {
												const blob = new Blob([res.data]);
												fileDownload(blob, 'Suppliers.xlsx');
												setExportBusy(false);
											})
											.catch(() => {
												setExportError(true);
												setExportBusy(false);
											});
									}}
								/>
							}
						/>
					</div>
					<div className={classes.actions}>
						<Typography
							{...themes.bodyRegular}
							alignItems="center"
							display="flex"
							color={color.neutral[8]}
							marginLeft="8px"
						>
							Sort By:
						</Typography>
						<SingleSelectDropDown
							label=""
							data={SortOptions}
							value={sortOption}
							onSelectCallback={setSortOption}
							isTextSelect
						/>
						{selectedRowNum.length > 0 && (
							<>
								<Typography
									{...themes.bodyRegular}
									alignItems="center"
									display="flex"
									color={color.text.dark}
									margin="0px 8px"
								>
									{`${selectedRowNum.length} item(s) are selected`}
								</Typography>
								{isAdmin && (
									<>
										<ToolTip
											width={200}
											tooltipText={
												isSamePlatform
													? ''
													: 'Sorry, cross-platform bulk editing is not supported now. Please select suppliers on the same platform.'
											}
											tooltipColor={color.shades.red[10]}
											tooltipTextColor={color.shades.red[70]}
											children={
												<span>
													<CustomButton
														id="edit"
														text="Edit"
														width={69}
														disabled={!isSamePlatform}
														startIcon={<EditOutlinedIcon />}
														onClickCallback={onEditClick}
													/>
												</span>
											}
										/>
										<ToolTip
											width={200}
											tooltipText={
												isSamePlatform
													? ''
													: 'Sorry, cross-platform bulk editing is not supported now. Please select suppliers on the same platform.'
											}
											tooltipColor={color.shades.red[10]}
											tooltipTextColor={color.shades.red[70]}
											children={
												<span>
													<CustomButton
														id="send-email"
														text="Send Email"
														width={113}
														disabled={!isSamePlatform}
														startIcon={<EmailOutlinedIcon />}
														href={`mailto:${selectedRow.map((r) => r.contact)}?body=Hi Team,`}
													/>
												</span>
											}
										/>
									</>
								)}
							</>
						)}
					</div>
					{supplierList && (
						<SupplierTable
							data={supplierList}
							totalCount={supplierInfo?.totalCount || 0}
							onRowSelectedCallback={setSelectedRowNum}
							onCellClickCallback={setActiveRow}
							onEditClickCallback={() => onEditClick(true)}
							onEmailClickHref={`mailto:${activeRow?.contact}?body=Hi Team,`}
							onPageChange={(a, b) => {
								setCurrPage(a);
								setRowsPerPage(b);
								setTimeout(() => {
									handleSearch();
								}, 500);
							}}
							page={currPage}
							rowsPerPage={rowsPerPage}
							onRowsPerPageChange={(a) => {
								setRowsPerPage(a);
								setTimeout(() => {
									handleSearch();
								}, 500);
							}}
						/>
					)}
					{!isRowEdit && selectedRow && (
						<Panel
							anchor="right"
							open={openPanel}
							title={selectedRow.length > 1 ? 'Bulk edit' : selectedRow[0]?.supplierName}
							onCloseCallback={onPanelClose}
							component={
								<SupplierEditPage
									key="supplier-edit"
									rows={selectedRow}
									isBulk={selectedRow.length > 1}
									supplierPlatforms={
										ttMasterData && ttMasterData?.platforms! && ttMasterData?.platforms!.filter((s) => s.key !== 2)
									}
									supplierScopeStatuses={ttMasterData && ttMasterData?.scopeStatuses!}
									isSave={setIsSave}
								/>
							}
						/>
					)}
					{isRowEdit && activeRow && (
						<Panel
							anchor="right"
							open={openPanel}
							title={activeRow.supplierName}
							onCloseCallback={onPanelClose}
							component={
								<SupplierEditPage
									rows={[activeRow]}
									isBulk={false}
									supplierPlatforms={
										ttMasterData && ttMasterData?.platforms! && ttMasterData?.platforms!.filter((s) => s.key !== 2)
									}
									supplierScopeStatuses={ttMasterData && ttMasterData?.scopeStatuses!}
									isSave={setIsSave}
								/>
							}
						/>
					)}
				</>
			)}
			{successAlertOpen && (
				<div className={classes.alert}>
					<CustomAlert
						open={successAlertOpen}
						timeout={5000}
						message="Saved successfully!"
						severity="success"
						onCloseCallback={() => setSuccessAlertOpen(false)}
					/>
				</div>
			)}
			{exportError && (
				<div className={classes.alert}>
					<CustomAlert
						open={exportError}
						timeout={5000}
						message="Unable to export data. Please try after sometime!"
						severity="error"
						onCloseCallback={() => setExportError(false)}
					/>
				</div>
			)}
		</div>
	);
};

const useStyles = makeStyles(() => ({
	topPadding: {
		paddingTop: '15px',
		'& #search': {
			backgroundColor: color.primary[100],
			color: color.text.light,
			minWidth: '40px !important',
			width: '40px',
			paddingRight: '5px',
			paddingBottom: '2px',
		},
		'& #clear': {
			minWidth: '40px !important',
			width: '40px',
			paddingRight: '5px',
			paddingBottom: '2px',
		},
	},
	dropdownContainer: {
		display: 'flex',
		alignItems: 'center',
		flexWrap: 'wrap',
		gap: '2px',
		// normalises the different filter controls to one common height/spacing
		'& .MuiFormControl-root': {
			margin: '4px',
		},
		'& .MuiOutlinedInput-root': {
			height: '40px',
			boxSizing: 'border-box',
		},
	},
	actions: {
		display: 'flex',
		'& .MuiFormControl-root': {
			margin: '8px 0px',
		},
		'& .MuiOutlinedInput-notchedOutline': {
			border: 'none',
		},
		'& .MuiButton-root': {
			...themes.bodyRegular,
			border: 'none',
			padding: '0px',
		},
		'& .MuiButton-root.Mui-disabled': {
			fontWeight: 400,
			border: 'none',
			padding: '0px',
		},
		'& .MuiButton-root:hover': {
			border: 'none',
		},
	},
	alert: {
		position: 'fixed',
		top: '10px',
		left: '50%',
		transform: 'translateX(-50%)',
	},
	export: {
		position: 'absolute',
		right: '15px',
		padding: '15px',
		cursor: 'pointer',
		fontSize: '30px !important',
		width: '22px',
		height: '22px',
		'&:hover': {
			background: '#0000000a',
			borderRadius: '50%',
		},
	},
}));
